import { GoogleReCaptchaProvider, useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { config } from '@/app/config';
import React, {
  Context,
  FC,
  ReactElement,
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

/**
 * The `id` prefix of the injected reCAPTCHA script, carrying the attempt number as its suffix.
 */
const SCRIPT_ID_PREFIX = 'recaptcha-script';

/**
 * The prefix of the release script `api.js` pulls in once it has loaded.
 * That second script carries no `id` of ours, so it is recognised by its source instead;
 * without it a failure of the release alone leaves the captcha unusable and unreloadable.
 */
const RELEASE_SCRIPT_SRC_PREFIX = 'https://www.gstatic.com/recaptcha/';

export interface ReCaptchaContextValue {
  ready: boolean;

  /**
   * Whether the current attempt is spent — its load failed, or it was retired by `markScriptUnusable`.
   * A caller waiting for readiness can then stop waiting instead of polling out its whole deadline.
   */
  scriptFailed: boolean;

  reloadScript: () => boolean;

  /**
   * Notes an attempt that is spent: the script loaded, yet the captcha never became able to issue a token.
   * No load error will ever arrive to say so, so without this `reloadScript` keeps declining
   * and every later click waits out the same dead attempt.
   */
  markScriptUnusable: () => void;
}

const ReCaptchaContext: Context<ReCaptchaContextValue> = createContext<ReCaptchaContextValue>({
  ready: false,
  scriptFailed: false,
  reloadScript: (): boolean => false,
  markScriptUnusable: (): void => undefined,
});

export const useReCaptchaReady = (): ReCaptchaContextValue => {
  return useContext(ReCaptchaContext);
};

interface ReCaptchaProviderProps {
  children: ReactNode;
}

const ReCaptchaReadinessDetector: FC<{ onReadyChange: (ready: boolean) => void }> = ({ onReadyChange }) => {
  const { executeRecaptcha } = useGoogleReCaptcha();

  useEffect(() => {
    if (executeRecaptcha) {
      console.info('ReCaptcha is ready via hook.', { executeRecaptcha });
      onReadyChange(true);
    } else {
      console.info('ReCaptcha is not ready yet.', { executeRecaptcha });
    }
  }, [executeRecaptcha, onReadyChange]);

  return null;
};

export const ReCaptchaProvider: FC<ReCaptchaProviderProps> = ({ children }: ReCaptchaProviderProps): ReactElement => {
  const [ready, setReady] = useState<boolean>(false);
  const [attempt, setAttempt] = useState<number>(0);
  const [scriptFailed, setScriptFailed] = useState<boolean>(false);

  /**
   * Mirrors `scriptFailed` for `reloadScript`, whose callers reach it through a ref of their own
   * and would otherwise decide from the value it captured a render earlier.
   */
  const scriptFailedRef = useRef<boolean>(false);

  const markScriptFailed = useCallback((failed: boolean): void => {
    scriptFailedRef.current = failed;
    setScriptFailed(failed);
  }, []);

  /**
   * Notes a script that never loaded. The library leaves such a load alone —
   * it neither reports the failure nor retries — so without this the captcha stays unusable until the page is reloaded.
   */
  useEffect((): (() => void) => {
    const handleLoadError = (event: Event): void => {
      const target: EventTarget | null = event.target;
      if (!(target instanceof HTMLScriptElement)) {
        return;
      }
      if (target.id.startsWith(SCRIPT_ID_PREFIX) || target.src.startsWith(RELEASE_SCRIPT_SRC_PREFIX)) {
        console.warn('The ReCaptcha script failed to load.', { src: target.src });
        markScriptFailed(true);
      }
    };
    window.addEventListener('error', handleLoadError, true);
    return (): void => {
      window.removeEventListener('error', handleLoadError, true);
    };
  }, [markScriptFailed]);

  /**
   * Publishes readiness for the Playwright suite: Google's own globals say when the captcha can issue a token,
   * not when this provider has published the executor, and that gap used to be a fixed guess.
   */
  useEffect((): void => {
    window.__captchaReady = ready;
  }, [ready]);

  /**
   * Loads the script again after it failed,
   * so access restored to the captcha service is picked up without a page reload.
   *
   * @return Whether a fresh load was started. A script that is merely still loading is left to finish.
   */
  const reloadScript = useCallback((): boolean => {
    if (!scriptFailedRef.current) {
      return false;
    }
    console.info('Reloading the ReCaptcha script after a failed load.');
    // The library drops `___grecaptcha_cfg` when it re-injects the script but leaves `window.grecaptcha`,
    // and the `ready` a half-loaded `api.js` installed queues its callback into the config that was dropped,
    // so readiness is never reported. Dropping the object as well gives the fresh script what a page reload does.
    delete window.grecaptcha;
    markScriptFailed(false);
    setAttempt((current: number): number => current + 1);
    return true;
  }, [markScriptFailed]);

  const markScriptUnusable = useCallback((): void => {
    markScriptFailed(true);
  }, [markScriptFailed]);

  const contextValue: ReCaptchaContextValue = useMemo(
    (): ReCaptchaContextValue => ({ ready, scriptFailed, reloadScript, markScriptUnusable }),
    [ready, scriptFailed, reloadScript, markScriptUnusable],
  );

  return (
    <GoogleReCaptchaProvider
      language={'ru'}
      reCaptchaKey={config.reCaptcha.siteKey}
      scriptProps={{
        async: true,
        defer: true,
        appendTo: 'head',
        nonce: undefined,
        id: `${SCRIPT_ID_PREFIX}-${attempt}`,
      }}>
      <ReCaptchaReadinessDetector onReadyChange={setReady} />
      <ReCaptchaContext.Provider value={contextValue}>{children}</ReCaptchaContext.Provider>
    </GoogleReCaptchaProvider>
  );
};
